import React from 'react';
import CloseIcon from '@assets/icons/admin-center/table/close.svg';
import SaveIcon from '@assets/icons/admin-center/table/save.svg';
import { TEST_IDS } from '@constants/testIds.ts';
import { NOT_SET_LABEL } from '@routes/sidebar/admin-center/___shared___/components/tables/constants.ts';
import { ActionsTableWrapper } from '@routes/sidebar/admin-center/___shared___/components/tables/regular/styled';
import { BuiltInStatusPill } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/components/built-in-status-pill';
import type { ColumnType } from 'antd/es/table';
import { isNil } from 'lodash';

export const renderCustomPillView = <R extends { key: React.Key }>(
    col: ColumnType<R>,
    opts: {
        isEditing: (row: R) => boolean;
        draftKey?: React.Key;
    },
): ColumnType<R> => {
    const baseRender = col.render as any;

    return {
        ...col,
        render: (value: boolean, record: R, index: number) => {
            const isRowEditing = opts.isEditing(record) || (!isNil(opts.draftKey) && record.key === opts.draftKey);

            if (isRowEditing && typeof baseRender === 'function') {
                return baseRender(value, record, index);
            }

            return <BuiltInStatusPill builtIn={value} />;
        },
    } as ColumnType<R>;
};

export const renderBuiltInStatusPill = <R extends { key: React.Key }>(col: ColumnType<R>): ColumnType<R> => ({
    ...col,
    render: (value: boolean) => <BuiltInStatusPill builtIn={value} />,
});

export const renderEditingRowActions = (opts: {
    onSave: () => void;
    onCancel: () => void;
    isSaveDisabled?: boolean;
}): React.ReactNode => (
    <ActionsTableWrapper $isEdit>
        <a
            onClick={() => {
                if (!opts.isSaveDisabled) {
                    opts.onSave();
                }
            }}
        >
            <SaveIcon data-cy={TEST_IDS.TENANT_CLIENT.SAVE_ICON} />
        </a>
        <a
            onClick={() => {
                opts.onCancel();
            }}
        >
            <CloseIcon data-cy={TEST_IDS.TENANT_CLIENT.CANCEL_ICON} />
        </a>
    </ActionsTableWrapper>
);

export const renderSelectorsViewBy = <R extends { key: React.Key }, K extends keyof R>(
    col: ColumnType<R>,
    opts: {
        isEditing: (row: R) => boolean;
        draftKey?: React.Key;
    },
    labelKey: K,
): ColumnType<R> => {
    const baseRender = col.render as any;

    return {
        ...col,
        render: (value: any, record: R, index: number) => {
            const isRowEditing = opts.isEditing(record) || (!isNil(opts.draftKey) && record.key === opts.draftKey);

            if (isRowEditing && typeof baseRender === 'function') {
                return baseRender(value, record, index);
            }

            const label = record[labelKey] as unknown;
            return <div>{(label as any) ?? NOT_SET_LABEL()}</div>;
        },
    };
};
